import i18next from 'i18next';
import dayjs from 'dayjs';
import type { ApprovalAttributes } from '@/types/approval';
import {
  formatQuantityInput,
  parseNumberInput,
} from '@/utils/approval/expensePreApproval';
import {
  toVehicleCostEmployee,
  VEHICLE_COST_HR_MISSING_MESSAGE,
} from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';

/** 법인카드 한도증액 — 레거시 appr_edit_forms_card_limit */
export const CARD_LIMIT_INCREASE_TYPE = 206;
/** 법인카드 재발급 — 레거시 appr_edit_forms_card_reissue */
export const CARD_REISSUE_TYPE = 207;

export const isCardLimitIncreaseType = (apvType: number): boolean =>
  apvType === CARD_LIMIT_INCREASE_TYPE;

export const isCardReissueType = (apvType: number): boolean =>
  apvType === CARD_REISSUE_TYPE;

export const isCorporateCardRequestType = (apvType: number): boolean =>
  isCardLimitIncreaseType(apvType) || isCardReissueType(apvType);

const SUBJECT_PREFIX: Record<number, string> = {
  [CARD_LIMIT_INCREASE_TYPE]: '법인카드 한도증액 신청의 건',
  [CARD_REISSUE_TYPE]: '법인카드 재발급 신청의 건',
};

/** 작성자 언어 제목 — 번역 리소스 로드 전이면 원문 */
export const buildCorporateCardRequestSubject = (
  apvType: number,
  name: string,
): string => {
  const prefix = SUBJECT_PREFIX[apvType] ?? '';
  return `${i18next.t(prefix) || prefix} (${name})`;
};

export type CorporateCardSource = {
  cardNumber: string;
  /** 만원 */
  currentLimit: number | null;
  /** 만원 */
  requestedLimit: number | null;
};

export type CorporateCardDraftSource = {
  display?: { cards?: CorporateCardSource[] | null } | null;
};

/** 직접 입력 선택값(레거시 option value "0") */
export const CARD_DIRECT_INPUT = '0';

export type CardNumberType = 'list' | 'direct';

export type CardReissueReason = 'broken' | 'lost' | 'other';
export type CardReportStatus = 'completed' | 'pending';

export const CARD_REISSUE_REASONS: {
  value: CardReissueReason;
  label: string;
}[] = [
  { value: 'broken', label: '파손 및 훼손' },
  { value: 'lost', label: '분실' },
  { value: 'other', label: '기타' },
];

export const CARD_REPORT_STATUSES: {
  value: CardReportStatus;
  label: string;
}[] = [
  { value: 'completed', label: '신고 완료' },
  { value: 'pending', label: '신고 미완료' },
];

export const CARD_REPORT_PENDING_ALERT =
  '카드를 분실한 경우, 신고 후 재발급 신청이 가능합니다.';

export type CorporateCardApplicant = {
  name: string;
  employeeNumber: string;
  /** 법인·사업부·부서를 공백으로 이은 값 */
  department: string;
  hireDate: string;
};

export type CorporateCardRequestFormState = {
  applicant: CorporateCardApplicant;
  /** 숫자만 */
  cardNumber: string;
  /** 카드를 고르기 전이면 null */
  cardNumberType: CardNumberType | null;
  startDate: string;
  endDate: string;
  /** 만원, 콤마 포함 입력값 */
  currentLimit: string;
  requestedLimit: string;
  reason: string;
  reissueReason: CardReissueReason | '';
  reasonText: string;
  reportStatus: CardReportStatus | '';
};

/** 레거시 formatCardNumber — 숫자 16자리까지, 4자리마다 하이픈 */
export const formatCardNumber = (raw: string): string =>
  raw
    .replace(/\D/g, '')
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, '$1-');

export const cardDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 16);

/** 시작 월 포함 3번째 달 말일. 레거시는 29~31일 시작이면 일자가 넘쳐 한 달 더 허용됐음 */
export const getCardLimitMaxEndDate = (startDate: string): string =>
  formatAsDate(dayjs(startDate).add(2, 'month').endOf('month'));

export const formatLimitInput = (value: string): string =>
  formatQuantityInput(value);

const limitToInput = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? formatLimitInput(String(value))
    : '';

export const limitsOfCard = (
  card: CorporateCardSource | undefined,
): Pick<CorporateCardRequestFormState, 'currentLimit' | 'requestedLimit'> => ({
  currentLimit: limitToInput(card?.currentLimit),
  requestedLimit: limitToInput(card?.requestedLimit),
});

export const createCorporateCardRequestState = (
  applicant: CorporateCardApplicant,
): CorporateCardRequestFormState => {
  const today = formatAsDate(dayjs());
  return {
    applicant,
    cardNumber: '',
    cardNumberType: null,
    startDate: today,
    endDate: today,
    currentLimit: '',
    requestedLimit: '',
    reason: '',
    reissueReason: '',
    reasonText: '',
    reportStatus: '',
  };
};

/** 내 인사 정보로 새 작성 상태. 인사 정보(사번)가 없으면 null */
export const corporateCardRequestStateFromMyInfo = (
  info: Parameters<typeof toVehicleCostEmployee>[0],
): CorporateCardRequestFormState | null => {
  const employee = toVehicleCostEmployee(info);
  if (!employee) return null;
  return createCorporateCardRequestState({
    name: employee.name,
    employeeNumber: employee.employeeNumber,
    department: employee.affiliation,
    hireDate: employee.enterDate,
  });
};

/** 이탈 경고 판정용 — 사용자가 고칠 수 있는 값만(신청자 정보는 자동) */
export const corporateCardRequestKey = (
  state: CorporateCardRequestFormState | null,
): string => {
  if (!state) return '';
  const { applicant: _applicant, ...editable } = state;
  return JSON.stringify(editable);
};

const isBlank = (value: string) => value.trim() === '';

/** 레거시 save_doc_card_limit·save_doc_card_reissue 검사 순서·문구 그대로(임시저장도 같은 검사) */
export const checkCorporateCardRequestBeforeSave = (
  apvType: number,
  state: CorporateCardRequestFormState | null | undefined,
): string | null => {
  if (!state) return VEHICLE_COST_HR_MISSING_MESSAGE;
  if (!state.cardNumber) {
    return state.cardNumberType === 'direct'
      ? '카드 번호를 입력해주세요.'
      : '카드 번호를 선택해주세요.';
  }
  if (isCardLimitIncreaseType(apvType)) {
    if (!state.startDate || !state.endDate) return '사용 기간을 입력해주세요.';
    if (isBlank(state.currentLimit)) return '현재 한도를 입력해주세요.';
    if (isBlank(state.requestedLimit)) return '최종 한도를 입력해주세요.';
    if (isBlank(state.reason)) return '사유를 입력해주세요.';
    return null;
  }
  if (!state.reissueReason) return '재발급 사유를 선택해주세요.';
  if (state.reissueReason === 'other' && isBlank(state.reasonText)) {
    return '기타 사유를 입력해주세요.';
  }
  if (state.reissueReason === 'lost') {
    if (!state.reportStatus) return '분실 신고 여부를 선택해주세요.';
    if (state.reportStatus === 'pending') {
      return '분실 신고 완료 후 재발급 신청이 가능합니다.';
    }
  }
  return null;
};

const limitOrNull = (value: string): number | null => {
  const n = parseNumberInput(value);
  return Number.isFinite(n) ? n : null;
};

export const buildCorporateCardRequestAttributes = (
  apvType: number,
  state: CorporateCardRequestFormState | null | undefined,
): ApprovalAttributes | null => {
  if (!state) return null;
  const { applicant } = state;
  const common = {
    employeeName: applicant.name,
    employeeNumber: applicant.employeeNumber,
    department: applicant.department || null,
    hireDate: applicant.hireDate,
    cardNumber: state.cardNumber,
    cardNumberType: state.cardNumberType,
  };
  if (isCardLimitIncreaseType(apvType)) {
    return {
      ...common,
      startDate: state.startDate,
      endDate: state.endDate,
      currentLimit: limitOrNull(state.currentLimit),
      requestedLimit: limitOrNull(state.requestedLimit),
      reason: state.reason.trim(),
    };
  }
  return {
    ...common,
    reason: state.reissueReason,
    reasonText:
      state.reissueReason === 'other' ? state.reasonText.trim() : null,
    reportStatus:
      state.reissueReason === 'lost' ? state.reportStatus || null : null,
  };
};

export type CorporateCardRequestAttributes = {
  employeeName: string;
  employeeNumber: string;
  department: string;
  hireDate: string;
  cardNumber: string;
  cardNumberType: string;
  startDate: string;
  endDate: string;
  currentLimit: number | null;
  requestedLimit: number | null;
  /** 206 증액 사유 / 207 재발급 사유 코드 */
  reason: string;
  reasonText: string;
  reportStatus: string;
};

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

// 백엔드가 날짜를 UTC 시각(…T15:00:00.000Z)으로 돌려줄 수 있음 — 잘라 쓰면 하루 밀림
const date = (v: unknown): string => (str(v) ? formatAsDate(str(v)) : '');

/** 단건조회 attributes. 사번·카드번호가 모두 없으면 null */
export const parseCorporateCardRequestAttributes = (
  attributes: unknown,
): CorporateCardRequestAttributes | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  if (!str(a.employeeNumber) && !str(a.cardNumber)) return null;
  return {
    employeeName: str(a.employeeName),
    employeeNumber: str(a.employeeNumber),
    department: str(a.department),
    hireDate: date(a.hireDate),
    cardNumber: cardDigits(str(a.cardNumber)),
    cardNumberType: str(a.cardNumberType),
    startDate: date(a.startDate),
    endDate: date(a.endDate),
    currentLimit: num(a.currentLimit),
    requestedLimit: num(a.requestedLimit),
    reason: str(a.reason),
    reasonText: str(a.reasonText),
    reportStatus: str(a.reportStatus),
  };
};

const pick = <T extends string>(
  value: string,
  options: { value: T }[],
): T | '' => options.find((o) => o.value === value)?.value ?? '';

export const corporateCardRequestAttributesToState = (
  apvType: number,
  saved: CorporateCardRequestAttributes,
): CorporateCardRequestFormState => {
  const base = createCorporateCardRequestState({
    name: saved.employeeName,
    employeeNumber: saved.employeeNumber,
    department: saved.department,
    hireDate: saved.hireDate,
  });
  const cardNumberType: CardNumberType | null = saved.cardNumber
    ? saved.cardNumberType === 'direct'
      ? 'direct'
      : 'list'
    : null;
  const restored = {
    ...base,
    cardNumber: saved.cardNumber,
    cardNumberType,
  };
  if (isCardLimitIncreaseType(apvType)) {
    return {
      ...restored,
      startDate: saved.startDate || base.startDate,
      endDate: saved.endDate || base.endDate,
      currentLimit: limitToInput(saved.currentLimit),
      requestedLimit: limitToInput(saved.requestedLimit),
      reason: saved.reason,
    };
  }
  return {
    ...restored,
    reissueReason: pick(saved.reason, CARD_REISSUE_REASONS),
    reasonText: saved.reasonText,
    reportStatus: pick(saved.reportStatus, CARD_REPORT_STATUSES),
  };
};

export const cardReissueReasonLabel = (value: string): string =>
  CARD_REISSUE_REASONS.find((o) => o.value === value)?.label ?? value;

export const cardReportStatusLabel = (value: string): string =>
  CARD_REPORT_STATUSES.find((o) => o.value === value)?.label ?? value;

export const formatLimitView = (value: number | null): string =>
  value === null ? '' : value.toLocaleString('en-US');
